import React from 'react';
import { motion } from 'framer-motion';
import { Award, Star, Zap, Target, BookOpen, CheckCircle2, Trophy, Flame } from 'lucide-react';

const badges = [
  { id: 1, title: 'First Steps', description: 'Completed your first lesson', icon: BookOpen, earned: true, color: 'from-blue-400 to-blue-600', earnedDate: '2026-04-02' },
  { id: 2, title: 'Quiz Master', description: 'Scored 90%+ on a quiz', icon: Star, earned: true, color: 'from-primary to-yellow-500', earnedDate: '2026-04-10' },
  { id: 3, title: 'Assignment Pro', description: 'Submitted all assignments on time', icon: CheckCircle2, earned: true, color: 'from-green-400 to-green-600', earnedDate: '2026-04-18' },
  { id: 4, title: 'Speed Learner', description: 'Completed 3 modules in a week', icon: Zap, earned: true, color: 'from-orange-400 to-red-500', earnedDate: '2026-04-22' },
  { id: 5, title: 'Top Performer', description: 'Rank in top 10% of the batch', icon: Trophy, earned: false, color: 'from-purple-400 to-purple-600', earnedDate: null },
  { id: 6, title: 'Streak Champion', description: 'Logged in 7 days in a row', icon: Flame, earned: false, color: 'from-rose-400 to-red-600', earnedDate: null },
  { id: 7, title: 'Project Hero', description: 'Submitted capstone project', icon: Target, earned: false, color: 'from-cyan-400 to-blue-500', earnedDate: null },
  { id: 8, title: 'Course Champion', description: 'Completed 100% of the course', icon: Award, earned: false, color: 'from-emerald-400 to-teal-600', earnedDate: null },
];

export default function BadgesSection() {
  const earned = badges.filter(b => b.earned).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Badges & Achievements</h2>
          <p className="text-muted-foreground text-sm mt-1">{earned}/{badges.length} badges earned</p>
        </div>
        <div className="glass rounded-xl px-4 py-2.5 border border-white/80 flex items-center gap-2">
          <Award className="w-4 h-4 text-primary" />
          <span className="font-bold text-sm">{earned} Earned</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="glass rounded-2xl border border-white/80 p-5">
        <div className="flex justify-between mb-2">
          <span className="text-sm font-semibold">Achievement Progress</span>
          <span className="text-sm font-bold text-primary">{Math.round((earned / badges.length) * 100)}%</span>
        </div>
        <div className="h-2.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(earned / badges.length) * 100}%` }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
            className="h-full bg-gradient-to-r from-primary to-yellow-500 rounded-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {badges.map((badge, i) => {
          const Icon = badge.icon;
          return (
            <motion.div
              key={badge.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.07, type: 'spring', stiffness: 300, damping: 20 }}
              whileHover={badge.earned ? { y: -4, scale: 1.04, transition: { duration: 0.2 } } : {}}
              className={`relative glass rounded-2xl border border-white/80 p-4 text-center flex flex-col items-center gap-2 ${!badge.earned ? 'opacity-45 grayscale' : 'shadow-sm'}`}
            >
              {badge.earned && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: i * 0.07 + 0.3, type: 'spring' }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm"
                >
                  <CheckCircle2 className="w-3 h-3 text-white" />
                </motion.div>
              )}
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${badge.color} flex items-center justify-center shadow-lg`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <p className="font-heading font-bold text-xs leading-tight text-center">{badge.title}</p>
              <p className="text-[10px] text-muted-foreground text-center leading-tight">{badge.description}</p>
              {badge.earnedDate && (
                <p className="text-[9px] text-primary font-semibold">{new Date(badge.earnedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
              )}
              {!badge.earned && (
                <p className="text-[9px] text-muted-foreground font-medium">Not yet earned</p>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}